// The case's sounds (spec web/case-opening), made by Web Audio, no files: the lid's latch (spec
// web/case-art), a tick per card passing the marker, a chime per rarity on landing, and a boom per
// blast. On by default, with a mute
// (Daniil, 2026-10-28); the dialog checks the `caseSound` pref before each call.
import type { Tier } from "./reel";

let context: AudioContext | undefined;

/** The page's audio context, made on the first sound (after a click, as browsers ask). */
function audio(): AudioContext | undefined {
  if (typeof AudioContext === "undefined") return undefined;
  context ??= new AudioContext();
  if (context.state === "suspended") void context.resume();
  return context;
}

function beep(frequency: number, at: number, length: number, volume: number, wave: OscillatorType): void {
  const sound = audio();
  if (sound === undefined) return;
  const oscillator = sound.createOscillator();
  const gain = sound.createGain();
  oscillator.type = wave;
  oscillator.frequency.value = frequency;
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(volume, at + 0.004);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + length);
  oscillator.connect(gain).connect(sound.destination);
  oscillator.start(at);
  oscillator.stop(at + length + 0.02);
}

/** A card passing the marker. */
export function tick(): void {
  const sound = audio();
  if (sound !== undefined) beep(1800, sound.currentTime, 0.03, 0.05, "square");
}

/** The notes each rarity lands with: a rarer tier rings longer and higher. */
export const CHIMES: Record<Tier, readonly number[]> = {
  common: [660, 880],
  rare: [660, 880, 990],
  epic: [660, 880, 1320],
  legendary: [523, 659, 784, 1046],
  mythic: [392, 523, 659, 784, 1046, 1318],
  jackpot: [784, 988, 1175, 1568, 1976, 2349],
};

export function chime(tier: Tier): void {
  const sound = audio();
  if (sound === undefined) return;
  const start = sound.currentTime;
  CHIMES[tier].forEach((note, at) => beep(note, start + at * 0.085, 0.3, 0.08, "triangle"));
}

/** The Theme Case's lid coming off (spec web/case-art): a latch's clack, then a rising shimmer. */
export function unlock(): void {
  const sound = audio();
  if (sound === undefined) return;
  const start = sound.currentTime;
  beep(150, start, 0.06, 0.12, "square");
  [880, 1175, 1568].forEach((note, at) => beep(note, start + 0.07 + at * 0.05, 0.25, 0.05, "sine"));
}

/** A blast: filtered noise that falls away, low and long for Mythic, short for fireworks. */
export function boom(size: "small" | "big"): void {
  const sound = audio();
  if (sound === undefined) return;
  const seconds = size === "small" ? 0.35 : 0.6;
  const buffer = sound.createBuffer(1, Math.floor(sound.sampleRate * seconds), sound.sampleRate);
  const samples = buffer.getChannelData(0);
  for (let at = 0; at < samples.length; at += 1) {
    samples[at] = (Math.random() * 2 - 1) * (1 - at / samples.length) ** 2;
  }
  const source = sound.createBufferSource();
  const filter = sound.createBiquadFilter();
  const gain = sound.createGain();
  filter.type = "lowpass";
  filter.frequency.value = size === "small" ? 1400 : 420;
  gain.gain.value = size === "small" ? 0.25 : 0.5;
  source.buffer = buffer;
  source.connect(filter).connect(gain).connect(sound.destination);
  source.start();
}
